/* @sravni/react-design-system version 0.2.71 */
import { __rest, __read, __assign } from 'tslib';
import * as React from 'react';
import { useStyles, useFormControlContext, useBoolean, useStateCallback, useMergedRef } from '@sravni/react-utils';
import { Cross, ArrowDown } from '@sravni/react-icons';
import '../../Dropdown/index.js';
import '../../TextInput/index.js';
import modules_be025dea from '../styles/Select.module.less.js';
import { Dropdown } from '../../Dropdown/src/Dropdown.js';
import { TextInput } from '../../TextInput/src/TextInput.js';

/**
 * @description The Select component allow user to select from a list of options
 *
 * @component
 * @example
 * ```jsx
 * <Select placeholder="Услуги" defaultValue={value} options={options} />
 * ```
 */
var Select = React.forwardRef(function (props, ref) {
    var autoFocus = props.autoFocus, defaultValue = props.defaultValue, value = props.value, mode = props.mode, options = props.options, invalid = props.invalid, disabled = props.disabled, readOnly = props.readOnly, icon = props.icon, leftIcon = props.leftIcon, _a = props.placement, placement = _a === void 0 ? 'bottom-start' : _a, _b = props.fixedWidth, fixedWidth = _b === void 0 ? true : _b, className = props.className, menuClassName = props.menuClassName, _c = props.withCrossIcon, withCrossIcon = _c === void 0 ? true : _c, _d = props.optionsSort, optionsSort = _d === void 0 ? true : _d, _e = props.flip, flip = _e === void 0 ? true : _e, mobile = props.mobile, onChange = props.onChange, onFocus = props.onFocus, onBlur = props.onBlur, restProps = __rest(props, ["autoFocus", "defaultValue", "value", "mode", "options", "invalid", "disabled", "readOnly", "icon", "leftIcon", "placement", "fixedWidth", "className", "menuClassName", "withCrossIcon", "optionsSort", "flip", "mobile", "onChange", "onFocus", "onBlur"]);
    var cx = useStyles(modules_be025dea);
    var classNames = cx(className, 'container');
    var inputRef = React.useRef(null);
    var menuRef = React.useRef(null);
    var formControlContext = useFormControlContext();
    var _f = __read(useBoolean(false), 2), visible = _f[0], setVisible = _f[1];
    var _g = __read(useBoolean(false), 2), focused = _g[0], setFocused = _g[1];
    var _h = __read(useStateCallback(value !== null && value !== void 0 ? value : defaultValue), 2), innerValue = _h[0], setInnerValue = _h[1];
    var _j = __read(React.useState(options || []), 2), innerOptions = _j[0], setInnerOptions = _j[1];
    var isMultiple = React.useMemo(function () { return mode === 'multiple'; }, [mode]);
    var optionsMap = React.useMemo(function () {
        if (!innerOptions) {
            return {};
        }
        return innerOptions.reduce(function (acc, o) {
            acc[o.value] = o;
            return acc;
        }, {});
    }, [innerOptions]);
    var displayValue = React.useMemo(function () {
        var _a;
        if (innerValue === undefined || innerValue === null) {
            return undefined;
        }
        if (isMultiple && Array.isArray(innerValue)) {
            if (innerValue.length === 0) {
                return undefined;
            }
            if (innerOptions) {
                return innerValue
                    .map(function (v) { var _a; return (_a = optionsMap[v]) === null || _a === void 0 ? void 0 : _a.label; })
                    .filter(Boolean)
                    .join(', ');
            }
        }
        return (_a = optionsMap[innerValue]) === null || _a === void 0 ? void 0 : _a.label;
    }, [innerOptions, isMultiple, innerValue, optionsMap]);
    var setValue = React.useCallback(function (value) {
        setInnerValue(value, function (val) {
            onChange === null || onChange === void 0 ? void 0 : onChange(val !== null && val !== void 0 ? val : '');
        });
    }, [onChange]);
    var hideMenu = React.useCallback(function () {
        var _a;
        setVisible.off();
        (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, []);
    var handleSelect = React.useCallback(function (value) {
        var _a;
        setValue(value);
        isMultiple ? (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus() : hideMenu();
    }, [isMultiple, setValue, hideMenu]);
    var handleFocus = React.useCallback(function (event) {
        if (!focused) {
            setFocused.on();
            onFocus === null || onFocus === void 0 ? void 0 : onFocus(event);
        }
    }, [focused, innerValue, onFocus]);
    var handleBlur = React.useCallback(function (event) {
        if (menuRef.current && event.relatedTarget === menuRef.current) {
            return;
        }
        setVisible.off();
        setFocused.off();
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(event);
    }, [visible, onBlur]);
    var handleCrossIconClick = React.useCallback(function () { return setValue(''); }, [setValue]);
    var handleClose = React.useCallback(function () {
        var _a;
        setVisible.off();
        mobile && ((_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.blur());
    }, [mobile, handleBlur]);
    var iconElement = React.useMemo(function () {
        var _a;
        if (icon) {
            return icon;
        }
        if (withCrossIcon && visible && innerValue !== undefined) {
            return React.createElement(Cross, { className: cx('icon'), onClick: handleCrossIconClick });
        }
        return (React.createElement(ArrowDown, { className: cx('icon', (_a = {},
                _a["isArrowTurned"] = visible,
                _a)) }));
    }, [visible, innerValue, icon, withCrossIcon, handleCrossIconClick]);
    React.useEffect(function () {
        if (value !== undefined) {
            setInnerValue(value);
        }
    }, [value]);
    React.useEffect(function () {
        setInnerOptions(options);
    }, [options]);
    //autoFocus should be applied only once
    React.useEffect(function () {
        var _a;
        autoFocus && ((_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus());
    }, []);
    return (React.createElement(Dropdown, { ref: menuRef, options: innerOptions, value: innerValue, className: menuClassName, mode: mode, placement: placement, disabled: disabled, readonly: readOnly, matchWidth: fixedWidth, mobile: mobile, flip: flip, tabIndex: -1, optionsSort: optionsSort, onOpen: setVisible.on, onClose: handleClose, onChange: handleSelect },
        React.createElement("div", { "data-qa": "Select", className: classNames, tabIndex: -1 },
            React.createElement(TextInput, __assign({}, restProps, { ref: useMergedRef(ref, inputRef), readOnly: true, invalid: formControlContext.invalid || invalid, disabled: formControlContext.disabled || disabled, value: displayValue, icon: iconElement, leftIcon: leftIcon, onFocus: handleFocus, onBlur: handleBlur })))));
});
Select.defaultProps = {
    withCrossIcon: true,
    fixedWidth: true,
    placement: 'bottom-start',
    optionsSort: true,
};
Select.displayName = 'Select';

export { Select };
//# sourceMappingURL=Select.js.map
